import { useEffect, useRef, useState } from 'react';
import {
  CopyIcon,
  DownloadIcon,
  Loader2Icon,
} from 'lucide-react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { toast } from 'sonner';
import {
  Artifact,
  ArtifactAction,
  ArtifactActions,
  ArtifactClose,
  ArtifactContent,
  ArtifactDescription,
  ArtifactHeader,
  ArtifactTitle,
} from '@/components/ai-elements/artifact';
import type { ActiveSourceReference } from '../chat-experience';
import { getChatErrorMessage } from '../chat-errors';
import {
  formatStreamingMetricsLabel,
  getDocumentContent,
  getDocumentTitle,
  getDocumentWordCount,
} from '../document-artifact';
import { useChatDocumentDocxPreview } from '../hooks/useChatDocumentDocxPreview';
import { isCodegenChatDocument } from '../codegen-document';
import { ChatDocumentPreview } from './ChatDocumentPreview';
import { CodegenDocxPanelPreview } from './CodegenDocxPanelPreview';
import { DOCX_MIME_TYPE } from './document-viewer/document-viewer-registry';
import { DocumentViewer } from './document-viewer/DocumentViewer';
import { ChatSidePanelShell } from './ChatSidePanelShell';
import { ChatDocumentKindIcon } from './ChatDocumentKindIcon';
import { useIsMobile } from '@/hooks/use-mobile';

type DocxPreviewBinding = {
  blob: Blob | null;
  blobUrl: string | null;
  downloadFilename: string | null;
  downloadUrl: string | null;
  error: string | null;
  retry: () => void;
  status: 'idle' | 'generating' | 'ready' | 'error';
};

type ChatDocumentPanelProps = {
  activeSourceReference: ActiveSourceReference | null;
  activeSourceReferences: ActiveSourceReference[];
  conversationId?: string;
  document: ChatDocumentDTO | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

function sanitizeFilename(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64) || 'document';
}

function hasDirectSourceDocument(document: ChatDocumentDTO) {
  return Boolean(document.sourceUrl);
}

function isDirectSourceView(document: ChatDocumentDTO) {
  if (
    isCodegenChatDocument(document)
    && document.mediaType === DOCX_MIME_TYPE
    && document.sourceUrl
  ) {
    return true;
  }

  return document.status === 'ready' && hasDirectSourceDocument(document);
}

function isDocxPreviewView(document: ChatDocumentDTO, docxPreview: DocxPreviewBinding) {
  return document.status === 'ready' && docxPreview.status !== 'idle';
}

async function downloadRemoteFile(sourceUrl: string, filename: string) {
  const response = await fetch(sourceUrl, { credentials: 'include' });

  if (!response.ok) {
    throw new Error('download_failed');
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  window.document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function PanelHeader({
  document,
  isDownloadDisabled,
  onClose,
  onCopy,
  onDownload,
}: {
  document: ChatDocumentDTO;
  isDownloadDisabled: boolean;
  onClose: () => void;
  onCopy: () => void;
  onDownload: () => void;
}) {
  const title = getDocumentTitle(document);

  return (
    <ArtifactHeader className="h-[3.25rem] border-border/50 bg-muted/20 px-4">
      <div className="flex min-w-0 items-center gap-3">
        <ChatDocumentKindIcon document={document} size="xs" />
        <div className="min-w-0">
          <ArtifactTitle className="truncate text-sm font-semibold">{title}</ArtifactTitle>
          <ArtifactDescription className="truncate text-xs">
            {document.status === 'streaming'
              ? formatStreamingMetricsLabel(getDocumentWordCount(document))
              : (document.filename ?? document.mediaType ?? 'Document conversationnel')}
          </ArtifactDescription>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <ArtifactActions>
          <ArtifactAction
            icon={CopyIcon}
            label="Copier le document"
            onClick={onCopy}
            tooltip="Copier"
          />
          <ArtifactAction
            disabled={isDownloadDisabled}
            icon={DownloadIcon}
            label="Télécharger le document"
            onClick={onDownload}
            tooltip="Télécharger"
          />
        </ArtifactActions>
        <ArtifactClose
          className="size-8 p-0 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          aria-label="Fermer le panneau"
        />
      </div>
    </ArtifactHeader>
  );
}

function PanelBody({
  activeSourceReference,
  activeSourceReferences,
  docxPreview,
  document,
}: {
  activeSourceReference: ActiveSourceReference | null;
  activeSourceReferences: ActiveSourceReference[];
  docxPreview: DocxPreviewBinding;
  document: ChatDocumentDTO;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const content = getDocumentContent(document).trim();
  const showDocxPreview = isDocxPreviewView(document, docxPreview);
  const isSourceDocument = isDirectSourceView(document);

  useEffect(() => {
    if (document.status !== 'streaming' || userScrolledRef.current) {
      return;
    }

    const element = containerRef.current;
    element?.scrollTo({ top: element.scrollHeight });
  }, [content, document.status]);

  if (
    isCodegenChatDocument(document)
    && document.mediaType === DOCX_MIME_TYPE
    && document.sourceUrl
  ) {
    return (
      <ArtifactContent className="relative flex-1 overflow-hidden bg-[#f1f3f5] p-0">
        <CodegenDocxPanelPreview
          filename={document.filename ?? document.title}
          sourceUrl={document.sourceUrl}
        />
      </ArtifactContent>
    );
  }

  if (isSourceDocument) {
    return (
      <ArtifactContent className="relative flex-1 overflow-hidden bg-[#f1f3f5] p-0">
        <DocumentViewer
          citation={activeSourceReference ? {
            anchor: activeSourceReference.anchor ?? null,
            excerpt: activeSourceReference.excerpt,
            reference: activeSourceReference,
          } : null}
          documentId={document.documentId}
          filename={document.filename ?? document.title}
          mediaType={document.mediaType ?? 'application/octet-stream'}
          onClose={() => undefined}
          sourceUrl={document.sourceUrl}
        />
      </ArtifactContent>
    );
  }

  if (showDocxPreview) {
    return (
      <ArtifactContent className="relative flex-1 overflow-hidden bg-[#f1f3f5] p-0">
        <ChatDocumentPreview
          activeReference={activeSourceReference}
          docxPreview={{
            blob: docxPreview.blob,
            error: docxPreview.error,
            onRetry: docxPreview.retry,
            status: docxPreview.status,
          }}
          document={document}
          sourceReferences={activeSourceReferences}
          variant="panel"
        />
      </ArtifactContent>
    );
  }

  return (
    <ArtifactContent className="relative flex-1 overflow-hidden bg-background p-0">
      <div
        ref={containerRef}
        className="relative h-full overflow-y-auto bg-muted/35 px-6 py-6"
        onScroll={() => {
          const element = containerRef.current;

          if (!element) {
            return;
          }

          const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
          userScrolledRef.current = !atBottom;
        }}
      >
        <div className="mx-auto flex h-full w-full max-w-[48rem]">
          <ChatDocumentPreview
            activeReference={activeSourceReference}
            className="h-full w-full shadow-[0_24px_60px_rgba(15,23,42,0.08)]"
            content={content}
            document={document}
            sourceReferences={activeSourceReferences}
            variant="panel"
          />
        </div>
      </div>
    </ArtifactContent>
  );
}

function DocumentPanelArtifact({
  activeSourceReference,
  activeSourceReferences,
  conversationId,
  document,
  isOpen,
  onClose,
}: {
  activeSourceReference: ActiveSourceReference | null;
  activeSourceReferences: ActiveSourceReference[];
  conversationId?: string;
  document: ChatDocumentDTO;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const docxPreview = useChatDocumentDocxPreview({
    conversationId,
    document,
    enabled: isOpen,
  });

  async function handleCopy() {
    const contentToCopy = getDocumentContent(document).trim();

    if (!contentToCopy) {
      toast.info('Aucun contenu à copier pour le moment.');
      return;
    }

    try {
      await navigator.clipboard.writeText(contentToCopy);
      toast.success('Contenu du document copié.');
    } catch {
      toast.error('Impossible de copier le contenu du document.');
    }
  }

  function handleMarkdownDownload() {
    const contentToDownload = getDocumentContent(document).trim();

    if (!contentToDownload) {
      toast.info('Aucun contenu à télécharger pour le moment.');
      return;
    }

    const filename = `${sanitizeFilename(getDocumentTitle(document))}.md`;
    const blob = new Blob([contentToDownload], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename;
    window.document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function triggerBlobDownload(blob: Blob, filename: string) {
    const objectUrl = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    window.document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }

  async function handleDownloadCurrentView() {
    if (isDownloading) {
      return;
    }

    if (isDirectSourceView(document) && document.sourceUrl) {
      setIsDownloading(true);

      try {
        const filename = document.filename ?? `${sanitizeFilename(getDocumentTitle(document))}.bin`;
        await downloadRemoteFile(document.sourceUrl, filename);
        toast.success('Téléchargement lancé.');
      } catch (error) {
        toast.error(getChatErrorMessage(error));
      } finally {
        setIsDownloading(false);
      }

      return;
    }

    if (isDocxPreviewView(document, docxPreview)) {
      if (docxPreview.status === 'ready' && docxPreview.blob && docxPreview.downloadFilename) {
        triggerBlobDownload(docxPreview.blob, docxPreview.downloadFilename);
        toast.success('Téléchargement lancé.');
        return;
      }

      toast.info('L’aperçu Word est encore en préparation.');
      return;
    }

    handleMarkdownDownload();
    toast.success('Téléchargement lancé.');
  }

  const isDownloadDisabled = isDownloading
    || document.status === 'streaming'
    || (isDocxPreviewView(document, docxPreview) && docxPreview.status === 'generating');

  return (
    <Artifact className="h-full rounded-none border-none bg-sidebar shadow-none">
      <PanelHeader
        document={document}
        isDownloadDisabled={isDownloadDisabled}
        onClose={onClose}
        onCopy={() => void handleCopy()}
        onDownload={() => void handleDownloadCurrentView()}
      />
      {docxPreview.status === 'generating' ? (
        <div className="flex items-center gap-2 border-b border-border/40 bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
          <Loader2Icon className="size-3.5 animate-spin" />
          Préparation de l’aperçu Word…
        </div>
      ) : null}
      {isDownloading ? (
        <div className="flex items-center gap-2 border-b border-border/40 bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
          <Loader2Icon className="size-3.5 animate-spin" />
          Téléchargement en cours…
        </div>
      ) : null}
      <PanelBody
        activeSourceReference={activeSourceReference}
        activeSourceReferences={activeSourceReferences}
        docxPreview={docxPreview}
        document={document}
      />
    </Artifact>
  );
}

export function ChatDocumentPanel({
  activeSourceReference,
  activeSourceReferences,
  conversationId,
  document,
  isOpen,
  onOpenChange,
}: ChatDocumentPanelProps) {
  const isMobile = useIsMobile();
  const isVisible = Boolean(document) && isOpen;

  if (isMobile) {
    if (!isVisible || !document) {
      return null;
    }

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-background">
        <DocumentPanelArtifact
          key={document.documentId}
          activeSourceReference={activeSourceReference}
          activeSourceReferences={activeSourceReferences}
          conversationId={conversationId}
          document={document}
          isOpen={isVisible}
          onClose={() => onOpenChange(false)}
        />
      </div>
    );
  }

  return (
    <ChatSidePanelShell isOpen={isVisible} testId="document-panel">
      {document ? (
        <DocumentPanelArtifact
          key={document.documentId}
          activeSourceReference={activeSourceReference}
          activeSourceReferences={activeSourceReferences}
          conversationId={conversationId}
          document={document}
          isOpen={isVisible}
          onClose={() => onOpenChange(false)}
        />
      ) : null}
    </ChatSidePanelShell>
  );
}
